// Preis-Rollout S3 (Plan 2026-10-20-preis-rollout-s3-plan.md): Klartext für alle Rollout-Fenster.
// Reine Anzeige — Zeitplan, Schreibweg und Wirkung rechnet das Backend; hier wird nur übersetzt.
import type {
  IstQuelle, Kennung, RegelAnzeige, RolloutStatus, ScopeWirkung, SetInfo, Zeitplan,
} from '@/api/priceRollout'
import { fmtEUR } from '@/lib/pricing-format'
import { a2GrundText } from '@/lib/a2-sperre'

const PORTAL: Record<string, string> = {
  base: 'Basis', amazon: 'Amazon', ebay: 'eBay', kaufland: 'Kaufland', fnac: 'Fnac', epages: 'ePages',
  nextore: 'Nextore', shopify: 'Shopify', metro: 'Metro',
}
export const portalText = (p: string): string => PORTAL[p] ?? p

/** Euro mit normalem Leerzeichen (Texte werden auch in Tooltips/Meldungen gezeigt). */
export const eur = (n: number): string => fmtEUR(n).replace(/ /g, ' ')

const DATUM = new Intl.DateTimeFormat('de-DE', { day: '2-digit', month: '2-digit', timeZone: 'Europe/Berlin' })
export const tagText = (iso: string): string => DATUM.format(new Date(iso))

// ---------------------------------------------------------------- Schreibziel

function drZiel(portal: string): string {
  return portal === 'base' ? 'DR-Grundpreis' : `DR-${portalText(portal)}-Preis`
}

/** Schreibweg des Backends (`selectWriterName`) als Ziel in Klartext; null = keine Regel. */
export function schreibzielText(portal: string, schreibweg: string | null | undefined): string {
  if (schreibweg === null || schreibweg === undefined) return 'keine Regel'
  if (schreibweg === 'dreamrobot') return drZiel(portal)
  if (schreibweg === 'shopify') return 'Shopify direkt'
  if (schreibweg === 'metro') return 'Metro direkt'
  if (schreibweg === 'amazon_spapi') return 'Amazon SP-API'
  if (schreibweg === 'ebay_trading') return 'eBay direkt'
  return 'kein Schreibweg'
}

/**
 * Schreibziel einer Regel für die LIVE-Rückfrage — dieselbe Wahl wie `selectWriterName` im Backend.
 * Metro hängt zusätzlich an der serverseitigen Metro-Schreibfreigabe, die die Oberfläche nicht kennt.
 */
export function regelSchreibziel(portal: string, adapter: string | null): string {
  if (portal === 'shopify') return 'Shopify direkt'
  if (portal === 'metro') return 'Metro direkt (nur mit Metro-Schreibfreigabe)'
  if (portal === 'amazon') return adapter === 'amazon_spapi' ? 'Amazon SP-API' : 'kein Schreibweg'
  if (portal === 'ebay' && adapter === 'ebay_trading') return 'eBay direkt (nur mit Basis-Rollout und eBay-Freigabe)'
  return adapter === null ? drZiel(portal) : 'kein Schreibweg'
}

// ---------------------------------------------------------------- Wirkung

export type WirkungTon = 'live' | 'protokoll' | 'aus'

const WIRKT_NICHT_GRUND: Partial<Record<ScopeWirkung, string>> = {
  keine_regel: 'keine Regel', regel_aus: 'Regel aus', gesperrt: 'Schreibweg gesperrt',
}

/** Was der Tageslauf mit diesem Rollout grundsätzlich tut (Regel + Freigaben), ohne Status/Termin. */
export function wirkungText(w: ScopeWirkung | undefined): { ton: WirkungTon; text: string } | null {
  if (w === undefined) return null
  if (w === 'live') return { ton: 'live', text: 'live – schreibt echte Preise' }
  if (w === 'dry_run') return { ton: 'protokoll', text: 'nur Protokoll' }
  return { ton: 'aus', text: `wirkt nicht: ${WIRKT_NICHT_GRUND[w] ?? w}` }
}

/** Live-Banner: Rollouts, die jetzt wirklich Preise schreiben werden (effektiv live und mit Termin). */
export function liveZaehlung(rows: readonly RolloutStatus[]): number {
  return rows.filter((r) => r.wirkung_konfiguriert === 'live' && r.zeitplan?.art === 'plan').length
}

// ---------------------------------------------------------------- Zeitplan

const BLOCK_GRUND: Record<string, string> = {
  pausiert: 'pausiert',
  fehler: 'Fehler – kein Schritt',
  not_aus: 'Not-Aus aktiv',
  regel_fehlt: 'keine Regel',
  regel_aus: 'Regel aus',
  wirkt_nicht: 'kein Schreibweg für dieses Portal',
  versand_exkl: 'Plus Versand: erst A2',
  exkl_versand_unbekannt: 'Plus Versand: Versand unbekannt',
  exkl_versand_geaendert: 'Plus Versand: Versand geändert, Neustart folgt',
  exkl_neu_starten: 'Plus Versand: neu starten',
  ek_unbekannt: 'EK fehlt – kein Schritt',
  bestaetigung_offen: 'Letzter Schritt wird noch bestätigt',
  kein_ist: 'Ist-Preis unbekannt',
  partner_inaktiv: 'Partner (Basis/eBay) pausiert',
  partner_blockiert: 'Partner (Basis/eBay) blockiert',
  modus_ungleich: 'Basis und eBay in verschiedenen Modi',
  amazon_gate: 'Amazon-Gebühren-Gate zu',
  amazon_mehrfachkonto: 'Amazon auf mehreren Konten',
  amazon_versand_unbestaetigt: 'Amazon: versandfrei nicht bestätigt',
  amazon_freigabe: 'Amazon: Artikel nicht freigegeben',
  amazon_budget: 'Amazon-Schreibbudget aufgebraucht',
  tagespruefung_aus: 'Tagesprüfung aus – kein automatischer Lauf',
  zu_lang: 'Ziel nicht absehbar',
  metro_band: 'Metro: kein gültiger Netto-Cent-Preis',
  grenze: 'Nächster Schritt verletzt die Preisgrenze',
  basis_rollout_starten: 'Nur DR geändert, eBay-Übernahme nicht bestätigt – Basis-Rollout starten',
  basis_fertig: 'Basis fertig – nur DR geändert, eBay-Übernahme nicht bestätigt; schließt im nächsten Tageslauf ab',
}

const WARTE_GRUND: Record<string, string> = {
  basis_holt_nach: 'Basis holt den eBay-Schritt nach',
  partner_nicht_faellig: 'wartet, bis die Basis fällig ist',
  ebay_nicht_gesteppt: 'wartet auf den eBay-Schritt',
  partner_inaktiv: 'wartet auf den Partner',
  ebay_holt_nach: 'eBay direkt holt den Basispreis nach – Basis wartet',
  basis_fertig: 'Basis fertig – eBay direkt holt den Endpreis nach',
}

export const blockGrundText = (g: string | null): string => (g === null ? 'kein Schritt' : BLOCK_GRUND[g] ?? g)

export type ZeitplanTon = 'plan' | 'protokoll' | 'block' | 'fertig'

/** „Nächster Schritt“-Zelle: Hauptzeile (Termin → Preis oder Grund) und Nebenzeile (Ziel, Wartegrund). */
export function zeitplanText(z: Zeitplan, now: Date = new Date()): { ton: ZeitplanTon; haupt: string; neben: string | null } {
  if (z.art === 'fertig') return { ton: 'fertig', haupt: z.endpreis === null ? 'Ziel erreicht' : `Ziel erreicht (${eur(z.endpreis)})`, neben: null }
  if (z.art === 'blockiert') {
    const neben = z.grund === 'tagespruefung_aus' && z.faellig_ab ? `fällig ab ${tagText(z.faellig_ab)}` : null
    return { ton: 'block', haupt: blockGrundText(z.grund), neben }
  }
  const wann = z.naechster_versuch === null ? '—'
    : new Date(z.naechster_versuch).getTime() - now.getTime() <= 2 * 60_000 ? 'sofort' : tagText(z.naechster_versuch)
  const haupt = `${wann} → ${z.naechster_preis === null ? '—' : eur(z.naechster_preis)}`
  const teile: string[] = []
  if (z.art === 'nur_protokoll') teile.push('nur Protokoll – kein Preis wird geschrieben')
  else if (z.ziel_am !== null) teile.push(`Ziel voraussichtlich ${tagText(z.ziel_am)}${z.schritte ? ` · ${z.schritte} Schritt${z.schritte === 1 ? '' : 'e'}` : ''}`)
  else if (z.ziel_grund === 'grenze') teile.push(`endet bei ${z.endpreis === null ? '—' : eur(z.endpreis)} (Preisgrenze)`)
  else if (z.ziel_grund === 'amazon_budget') teile.push('Termine ungeklärt – Amazon schreibt höchstens einen Preis je Lauf')
  else if (z.ziel_grund === 'zu_lang') teile.push('Ziel nicht absehbar')
  if (z.grund === 'tagespruefung_aus') teile.push('danach kein automatischer Lauf (Tagesprüfung aus)')
  if (z.warte_grund) teile.push(WARTE_GRUND[z.warte_grund] ?? z.warte_grund)
  return { ton: z.art === 'nur_protokoll' ? 'protokoll' : 'plan', haupt, neben: teile.length ? teile.join(' · ') : null }
}

// ---------------------------------------------------------------- Regel

export function schrittKlartext(r: { max_step_ct: number; max_step_pct?: number | null }): string {
  return `max. ${eur(r.max_step_ct / 100)}${r.max_step_pct == null ? '' : ` oder ${Math.round(r.max_step_pct * 1000) / 10} %`}`
}

export function taktKlartext(r: { interval_days: number }): string {
  return r.interval_days === 1 ? 'jeden Tag' : `alle ${r.interval_days} Tage`
}

export function pauseKlartext(r: { cooldown_days: number }): string {
  return r.cooldown_days === 0 ? 'keine' : `${r.cooldown_days} Tag${r.cooldown_days === 1 ? '' : 'e'}`
}

/** Schritt, Takt und Sperre einer Regel in Klartext (Anteil `max_step_pct` als Bruch, 0.3 = 30 %). */
export function regelKlartext(
  r: Pick<RegelAnzeige, 'max_step_ct' | 'interval_days' | 'cooldown_days'> & { max_step_pct?: number | null },
): string {
  const pct = r.max_step_pct == null ? '' : ` bzw. ${Math.round(r.max_step_pct * 1000) / 10} %`
  const takt = r.interval_days === 1 ? 'jeden Tag ein Schritt' : `alle ${r.interval_days} Tage ein Schritt`
  const sperre = r.cooldown_days === 0 ? 'keine Sperre' : `danach ${r.cooldown_days} Tag${r.cooldown_days === 1 ? '' : 'e'} gesperrt`
  return `höchstens ${r.max_step_ct} ct${pct} je Schritt · ${takt} · ${sperre}`
}

/** „Änderung zu klein“-Schwelle (Dead-Band) in Klartext. */
export function totbandKlartext(ct: number, pct: number): string {
  return `Zieländerungen unter ${ct} ct bzw. ${Math.round(pct * 1000) / 10} % gelten als zu klein – das alte Ziel bleibt`
}

// ---------------------------------------------------------------- Historie

const HISTORY_GRUND: Record<string, string> = {
  rollout_start: 'Start',
  rollout_step: 'Schritt',
  rollout_done: 'Ziel erreicht',
  dry_run_preview: 'Vorschau (nur Protokoll)',
  reconcile_applied: 'Schritt bestätigt',
  reconcile_discarded: 'Schritt verworfen',
  reconcile_foreign_change: 'Beim Bestätigen: Preis außerhalb geändert',
  paused_foreign_change: 'Preis außerhalb geändert – pausiert',
  write_foreign_change: 'Preis außerhalb geändert',
  exkl_bis_A2: 'Plus Versand: erst A2',
  input_sanity_failed: 'Eingabeprüfung fehlgeschlagen',
  infeasible_boundary: 'Ziel außerhalb der Preisgrenzen',
  infeasible_after_rule_change: 'Nach Regeländerung außerhalb der Preisgrenzen',
  write_delivery_unknown: 'Schreiben unbestätigt – wird geprüft',
  delivery_unknown_exhausted: 'Schreiben dauerhaft unbestätigt',
  write_error: 'Schreibfehler',
  write_transient_retry: 'Schreiben kurz fehlgeschlagen – neuer Versuch',
  write_pre_send_transient: 'Schreiben kurz fehlgeschlagen – neuer Versuch',
  write_pre_send_terminal: 'Schreiben abgelehnt',
  write_invalid_portal: 'kein Schreibweg für dieses Portal',
  write_not_found: 'Angebot nicht gefunden',
  coupled_mode_mismatch: 'Basis und eBay in verschiedenen Modi',
  coupled_wait: 'wartet auf den eBay-Schritt',
  ebay_folgt_basis: 'Nur DR geändert, eBay-Übernahme nicht bestätigt (kein eigener DR-Preis)',
  ebay_folgt_basis_done: 'Nur DR geändert, eBay-Übernahme nicht bestätigt – fertig mit der Basis',
  ebay_intent: 'eBay direkt: Preisänderung vorgemerkt',
  ebay_trading_call: 'eBay direkt: Aufruf gezählt (Tagesdeckel)',
  ebay_synchron: 'eBay direkt: steht schon auf dem Basispreis',
  ebay_direct_hold: 'eBay direkt angehalten',
  ebay_no_listing: 'eBay direkt: kein aktives Angebot gefunden',
  ebay_no_listing_paused: 'eBay direkt: 72 h kein Angebot – pausiert',
  ebay_canary_blocked: 'eBay direkt: Artikel nicht freigegeben',
  ebay_write_budget: 'eBay direkt: Schreibbudget erreicht',
  coupled_bounds_mismatch: 'Basispreis außerhalb der eBay-Grenzen – beide pausiert',
  live_read_failed: 'Live-Preis nicht lesbar',
  live_read_failed_paused: 'Live-Preis 72 h nicht lesbar – pausiert',
  ek_unknown: 'EK fehlt – kein Schritt',
  set_ek_unknown: 'Set-EK unvollständig – kein Schritt',
  target_changed: 'Ziel geändert',
  lease_lost: 'Lauf abgebrochen (anderer Lauf aktiv)',
  kill: 'Not-Aus',
  kill_active: 'Not-Aus aktiv',
  dr_doc_failed: 'DR-Preisdokumentation fehlgeschlagen',
  amazon_fee_gate: 'Amazon-Gebühren-Gate zu',
  amazon_multi_account: 'Amazon auf mehreren Konten',
  amazon_canary_pending_manual_review: 'Amazon: manuelle Prüfung offen',
  amazon_canary_delivery_unknown: 'Amazon: Schreiben unbestätigt',
  amazon_canary_shipping_unconfirmed: 'Amazon: versandfrei nicht bestätigt',
  amazon_canary_write_budget: 'Amazon: Schreibbudget je Lauf erreicht',
  amazon_canary_allowlist_missing: 'Amazon: Freigabeliste fehlt',
  amazon_canary_allowlist_malformed: 'Amazon: Freigabeliste fehlerhaft',
  amazon_canary_not_allowlisted: 'Amazon: Artikel nicht freigegeben (Allowlist)',
  amazon_canary_adapter: 'Amazon: falscher Schreibweg',
  amazon_canary_identity: 'Amazon: Konto/SellerSKU unklar',
  amazon_canary_marketplace: 'Amazon: falscher Marktplatz',
  amazon_canary_offer_structure: 'Amazon: Angebot hat unerwartete Struktur',
  amazon_canary_fee_unavailable: 'Amazon: Gebühren nicht abrufbar',
  amazon_canary_fee_status: 'Amazon: Gebührenabfrage fehlgeschlagen',
  amazon_canary_fee_currency: 'Amazon: Gebühren in falscher Währung',
  amazon_canary_fee_incomplete: 'Amazon: Gebühren unvollständig',
  amazon_canary_fee_fulfillment_unknown: 'Amazon: Versandart unklar',
  amazon_canary_fee_budget_exceeded: 'Amazon: Gebühren über Budget',
  amazon_canary_patch_not_accepted: 'Amazon: Preisänderung nicht angenommen',
  amazon_canary_b2b_changed: 'Amazon: B2B-Preis verändert',
  amazon_canary_convergence_timeout: 'Amazon: neuer Preis nicht rechtzeitig sichtbar',
}

export const historyGrundText = (reason: string): string => HISTORY_GRUND[reason] ?? a2GrundText(reason) ?? reason

const WRITER: Record<string, string> = {
  dreamrobot: 'DreamRobot', amazon_spapi: 'Amazon SP-API', shopify: 'Shopify', metro: 'Metro', ebay_trading: 'eBay direkt', none: '—',
}
export const writerText = (w: string | null): string => (w === null ? '—' : WRITER[w] ?? w)

// ---------------------------------------------------------------- Kennung

export function setText(s: SetInfo): string {
  if (s.art === 'einzel') return 'Einzelstück'
  if (s.art === 'menge') return `×${s.menge}`
  return `Set ×${s.teile}`
}

/** Text für die clientseitige Suche (Stern, DR, Name, Grundartikel), klein geschrieben. */
export function kennungSuchtext(k: Kennung | null | undefined, sku: string | null, name: string | null): string {
  const teile = [sku, name, k?.sku, k?.stern, k?.name, k?.grundartikel?.sku, k?.grundartikel?.stern, k?.grundartikel?.name]
  return teile.filter((t): t is string => !!t).join(' ').toLowerCase()
}

const IST_QUELLE: Record<IstQuelle, string> = {
  ausstehend: 'ausstehend – letzter Schritt wird bestätigt', zuletzt_gesetzt: 'zuletzt gesetzt', angebot: 'aktuelles Angebot', dr_preis: 'DR-Preis',
  erbt_basis: 'Nur DR geändert, eBay-Übernahme nicht bestätigt (kein eigener DR-Preis)',
}
export const istQuelleText = (q: IstQuelle | null | undefined): string | null => (q ? IST_QUELLE[q] ?? q : null)

// Verkäuferkonten (eBay/Amazon) als kurze Marke; unbekannte Konten zeigen ihren Schlüssel.
const KONTO_KURZ: Record<string, string> = {
  'drs_e-commerce': 'DRS', 'ideal-deko': 'Ideal-Deko', 'b2c-trends': 'B2C-Trends', '24-trends': '24-Trends',
  stahlia: 'Stahlia', drs: 'DRS', neuhall: 'Neuhall',
}
export const kontoKurz = (konto: string): string => KONTO_KURZ[konto] ?? konto
